import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback } from 'react';
import { SearchInput } from 'src/components/SerchInput';
import i18next from 'i18next';
import {
    selectShippingQuery,
    selectShippingSelectedOrders,
    selectIncludeSummary
} from './ShippingPlanningSelectors';
import {
    updateOrdersToShipQuery,
    updateSelectedOrdersIds,
    updateIncludeSummary
} from './ShippingPlanningActions';
import {
    Tooltip,
    IconButton,
    FormControlLabel,
    Radio,
    RadioGroup,
    Select,
    MenuItem,
    Divider,
    ListItem,
    Checkbox
} from '@material-ui/core';
import { tooltipEnterDelay } from 'src/config';
import PrintIcon from '@material-ui/icons/Print';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ClearIcon from '@material-ui/icons/Clear';
import { RequestMethod } from 'src/utils/request';
import { useRequest } from 'src/common/useRequestHook';
import { API_URLS } from 'src/common/ApiService';
import { NotificationVariant } from 'src/models/Notification';
import { showNotification } from 'src/common/notifications/NotificationsActions';
import { orderStatus, country } from 'src/common/enums/enums';
import { Shipping } from './ShippingPlanningService';
import { useEnums } from 'src/common/enums/useEnumsHook';
import { resolveEnumValue } from 'src/utils/enums';

export const ShippingOrdersPrintingAppBarView: React.FC = () => {
    const dispatch = useDispatch();
    const query = useSelector(selectShippingQuery);
    const selectedOrdersIds = useSelector(selectShippingSelectedOrders);
    const includeSummary = useSelector(selectIncludeSummary);
    const enums = useEnums();
    const [updateOrdersStatus, { inProgress }] = useRequest(
        RequestMethod.POST,
        API_URLS.ordersUpdateStatus
    );

    // Get all available countries from enums
    const availableCountries = Object.keys(country);

    const selectedCountries = query.countries || [];

    const onChange = useCallback(
        (value: string) => {
            dispatch(updateOrdersToShipQuery({ ...query, search: value }));
        },
        [dispatch, query]
    );

    const handleCountryChange = (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        dispatch(
            updateOrdersToShipQuery({
                ...query,
                countries: event.target.value as string[]
            })
        );
    };

    const handleResetCountryFilter = () => {
        dispatch(
            updateOrdersToShipQuery({
                ...query,
                countries: undefined
            })
        );
    };

    const handlePrintAndChangeStateClick = () => {
        updateOrdersStatus({
            ids: selectedOrdersIds,
            status: orderStatus.READY_TO_SHIP
        })
            .then(() => {
                window.print();

                dispatch(
                    showNotification({
                        id: `set_orders_ready_to_ship_${Date.now()}`,
                        variant: NotificationVariant.Success,
                        text: i18next.t(
                            'shippingPlanning.setOrdersReadyToShipSuccess'
                        )
                    })
                );
                dispatch(updateSelectedOrdersIds([]));
                dispatch(updateOrdersToShipQuery({ ...query }));
            })
            .catch(err => {
                if (err.isNotificationError) {
                    return;
                }
                dispatch(
                    showNotification({
                        id: `set_orders_ready_to_ship_${Date.now()}`,
                        variant: NotificationVariant.Error,
                        text: i18next.t(
                            'shippingPlanning.setOrdersReadyToShipError'
                        )
                    })
                );
            });
    };

    const handleShippingChange = evt => {
        dispatch(
            updateOrdersToShipQuery({
                ...query,
                shipping: evt.currentTarget.value
            })
        );
    };

    return (
        <>
            <SearchInput
                value={query.search}
                placeholder={i18next.t('Order number, name, email')}
                onChange={onChange}
            />
            <Select
                multiple
                value={query.countries ?? []}
                onChange={handleCountryChange}
                displayEmpty
                style={{
                    color: 'white',
                    minWidth: '100px',
                    maxWidth: '100px',
                    marginLeft: '10px'
                }}
                renderValue={selected => {
                    const countries = selected as string[];
                    if (!countries || countries.length === 0) {
                        return i18next.t('shippingPlanning.countries');
                    }

                    return countries.join(', ');
                }}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: '100%'
                        }
                    },
                    getContentAnchorEl: null,
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                    }
                }}
            >
                {availableCountries.map(countryCode => (
                    <MenuItem key={countryCode} value={countryCode}>
                        {resolveEnumValue(enums.country.enum, countryCode)}
                    </MenuItem>
                ))}
                {selectedCountries.length > 0 && (
                    <>
                        <Divider />
                        <ListItem
                            button
                            onClick={handleResetCountryFilter}
                            style={{
                                justifyContent: 'center',
                                padding: '8px 16px',
                                backgroundColor: '#f5f5f5'
                            }}
                        >
                            <ClearIcon
                                fontSize="small"
                                style={{ marginRight: '8px' }}
                            />
                            {i18next.t('Clear')}
                        </ListItem>
                    </>
                )}
            </Select>
            <RadioGroup
                row
                style={{ flexWrap: 'nowrap', padding: '0 10px' }}
                value={query.shipping}
                onChange={handleShippingChange}
            >
                <FormControlLabel
                    value={Shipping.PERSONAL_PICKUP}
                    control={<Radio />}
                    label={i18next.t('shippingPlanning.radioPersonalPickup')}
                />
                <FormControlLabel
                    value={Shipping.COURIER_Z}
                    control={<Radio />}
                    label={i18next.t('shippingPlanning.radioCourierZemianska')}
                />
                <FormControlLabel
                    value={Shipping.COURIER_H}
                    control={<Radio />}
                    label={i18next.t('shippingPlanning.radioCourierHamricky')}
                />
                {/* <FormControlLabel
                    value={Shipping.CARTAGE}
                    control={<Radio />}
                    label={i18next.t('shippingPlanning.radioCartage')}
                /> */}
            </RadioGroup>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={includeSummary}
                        onChange={e =>
                            dispatch(updateIncludeSummary(e.target.checked))
                        }
                        style={{ color: 'white' }}
                    />
                }
                label={i18next.t('shippingPlanning.includeSummary')}
                style={{ color: 'white', marginLeft: '10px' }}
            />
            <Tooltip
                title={i18next.t('shippingPlanning.printAndChangeState')}
                enterDelay={tooltipEnterDelay}
            >
                <IconButton
                    color="inherit"
                    onClick={handlePrintAndChangeStateClick}
                    disabled={!selectedOrdersIds.length || inProgress}
                >
                    <PrintIcon />
                    <LocalShippingIcon
                        style={{
                            width: '0.7em',
                            marginTop: '10px'
                        }}
                    />
                </IconButton>
            </Tooltip>
        </>
    );
};
